import { themes } from "@caliu/styles/themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { track, AnalyticsEvent } from "../../lib/analytics";
import {
  ONBOARDING_STEPS,
  WELCOME_INTRO_SUBTITLE_STEP,
  WELCOME_INTRO_TITLE_STEP,
  hasPlayedWelcomeIntro,
  isOnboardingCompleted,
  markOnboardingCompleted,
  markWelcomeIntroPlayed,
  shouldPlayWelcomeIntro,
  nextOnboardingStep,
  previousOnboardingStep,
  welcomeIntroTimeline,
  type OnboardingStep,
  type WelcomeIntroChar,
  type WelcomeIntroTimeline,
} from "../../lib/onboarding";
import { canUseTheme } from "../../lib/pro";
import { prefetchProOffering } from "../../lib/purchases";
import { useNoteLimit } from "../../hooks/useNoteLimit";
import { useAuthStore } from "../../store/auth";
import { useIsPro } from "../../store/subscription";
import { useUIStore, type Theme } from "../../store/ui";
import {
  ProFeatureListBox,
  ProLockup,
  ProPlanCards,
  ProPurchaseCta,
  useProPurchase,
  type ProPurchaseState,
} from "../PaywallShared";
import { useIsDark } from "../../hooks/useIsDark";
import { ThemePreviewCard } from "../settings/ThemePreviewCard";
import { SegmentedControl } from "../ui/SegmentedControl";
import { AnimatedCaliuLogo } from "../ui/AnimatedCaliuLogo";
import { Button } from "../ui/Button";
import { Icon } from "@/components/ui/Icon";

/**
 * Mounted once at the app root (next to PaywallDialog). Opens onboarding when
 * an authenticated user lands in /app without the completion flag, and renders
 * the overlay whenever the store says it's open (first run or replay).
 */
export function OnboardingGate() {
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const localMode = useAuthStore((s) => s.localMode);
  const open = useUIStore((s) => s.onboardingOpen);
  const openOnboarding = useUIStore((s) => s.openOnboarding);
  const inApp = pathname.startsWith("/app");

  // Local mode counts as "in the app": onboarding runs exactly once whichever
  // door the user entered by (the account-less front door sets the same flag).
  useEffect(() => {
    if (inApp && (user || localMode) && !isOnboardingCompleted()) openOnboarding();
  }, [inApp, user, localMode, openOnboarding]);

  if (!open || !inApp || !(user || localMode)) return null;
  return <OnboardingOverlay />;
}

/**
 * First-run onboarding: welcome → live markdown demo → theme → Pro finale.
 * Mirrors the Swift flow (minus Swift's density step). Full-screen at z-[150]:
 * above the app shell, below dialogs (z-200) so the paywall opens on top of it.
 */
export function OnboardingOverlay({ frontDoor = false }: { frontDoor?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const closeOnboarding = useUIStore((s) => s.closeOnboarding);
  const replay = useUIStore((s) => s.onboardingReplay);
  const enterLocalMode = useAuthStore((s) => s.enterLocalMode);
  const hasUser = useAuthStore((s) => s.user !== null);
  const isPro = useIsPro();

  const [step, setStep] = useState<OnboardingStep>("welcome");
  // Purchase engine lives at overlay level so the pro step's plan cards (in
  // the scrolling content) and the buy CTA (pinned in the footer, mirroring
  // Swift) share one selection. `onPurchased` is a no-op: the `isPro` flip
  // re-renders the step to its send-off and the footer back to "Start
  // writing".
  const proPurchase = useProPurchase(() => {});
  // Direction of the last step change — drives the transition (forward exits
  // to the left and enters from the right; back mirrors it). Null until the
  // first navigation so the initial step doesn't slide in on mount (the
  // overlay's own fade-in covers the entrance).
  const [direction, setDirection] = useState<"forward" | "back" | null>(null);
  // Two-phase step change: `pendingStep` plays the exit animation on the
  // current step; `onAnimationEnd` then commits it and the remount (keyed by
  // step) plays the enter animation.
  const [pendingStep, setPendingStep] = useState<OnboardingStep | null>(null);
  // Plays the whole-overlay fade-out; `onAnimationEnd` then closes for real.
  const [closing, setClosing] = useState(false);
  const completedRef = useRef(false);
  const startedRef = useRef(false);

  // Welcome intro (the logo grows, the copy is typed, then the chrome fades
  // in). Runs once per mount of the overlay: "running" until its timeline
  // ends ("played", the chrome fades in) or it is cut short ("skipped": a
  // click anywhere, leaving the step, or reduced motion, which never starts
  // it). Coming back to the welcome step shows the finished state.
  // Also once per browser: a later tour (after a sign-out) opens on the
  // finished state, unless it is a replay from Settings.
  const [intro, setIntro] = useState<WelcomeIntroState>(() =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    !shouldPlayWelcomeIntro(hasPlayedWelcomeIntro(), replay)
      ? "skipped"
      : "running",
  );
  useEffect(() => {
    if (intro === "running") markWelcomeIntroPlayed();
  }, [intro]);
  const welcomeTitle = t("onboarding.welcomeTitle");
  const welcomeSubtitle = t("onboarding.welcomeSubtitle");
  const introTimeline = useMemo(
    () => welcomeIntroTimeline(welcomeTitle, welcomeSubtitle),
    [welcomeTitle, welcomeSubtitle],
  );
  const introFinish = introTimeline.finish;
  useEffect(() => {
    if (intro !== "running") return;
    const timer = setTimeout(() => setIntro("played"), introFinish * 1000);
    return () => clearTimeout(timer);
  }, [intro, introFinish]);
  const skipIntro = useCallback(
    () => setIntro((state) => (state === "running" ? "skipped" : state)),
    [],
  );
  // Only ever while the welcome step is current, so a step change mid-intro
  // can never leave the next step's header or footer hidden.
  const chromeHidden = step === "welcome" && intro === "running";
  // Hidden chrome keeps its layout space and is inert (no pointer, no focus).
  // The 0.8 s fade belongs to the intro's own ending; a skip shows it at once.
  const chromeClass = [
    "transition-opacity ease-out",
    intro === "played" ? "duration-[800ms]" : "duration-0",
    chromeHidden ? "opacity-0" : "opacity-100",
  ].join(" ");

  // Fire once when the overlay first mounts (first-run or replay).
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    track(AnalyticsEvent.OnboardingStarted);
  }, []);

  const navigateTo = useCallback(
    (target: OnboardingStep, dir: "forward" | "back") => {
      skipIntro();
      setDirection(dir);
      // Reduced motion: the exit animation never runs, so `onAnimationEnd`
      // would never commit the step — swap immediately instead.
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setStep(target);
      } else {
        setPendingStep(target);
      }
    },
    [skipIntro],
  );

  // Warm the RevenueCat offering as soon as onboarding opens (same as the
  // settings menu) so the Pro step's plan cards render without a jump.
  useEffect(() => {
    if (!isPro) prefetchProOffering();
  }, [isPro]);

  const complete = useCallback(
    (outcome: "purchased" | "continued_free" | "skipped") => {
      if (completedRef.current) return;
      completedRef.current = true;
      // No server account (front door OR a local-mode replay): finishing
      // onboarding — by any path, skip included — lands in local mode. Fired
      // here, not on step advance, so the overlay never unmounts mid-flow when
      // AppPage re-renders on the flag. Sync is not decided here any more: it
      // follows from signing in, and the sync step's card is the door.
      if (!hasUser) {
        enterLocalMode();
      }
      markOnboardingCompleted();
      track(AnalyticsEvent.OnboardingCompleted, {
        reached_step: step,
        outcome,
        is_pro: isPro,
        replay,
      });
      // Reduced motion: the fade-out never runs, so close immediately.
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        closeOnboarding();
      } else {
        setClosing(true);
      }
    },
    [step, isPro, replay, closeOnboarding, hasUser, enterLocalMode],
  );

  const goNext = useCallback(() => {
    if (pendingStep) return; // mid-transition
    const next = nextOnboardingStep(step);
    if (next) navigateTo(next, "forward");
    // Reaching the end via the primary CTA on the Pro step means the user is
    // Pro (they just purchased) — otherwise the footer shows "continue free".
    else complete(isPro ? "purchased" : "continued_free");
  }, [step, pendingStep, navigateTo, complete, isPro]);

  const goBack = useCallback(() => {
    if (pendingStep) return; // mid-transition
    const previous = previousOnboardingStep(step);
    if (previous) navigateTo(previous, "back");
  }, [step, pendingStep, navigateTo]);

  // The Pro step renders the purchase inline (see ProStep), so its buy CTA
  // isn't the footer button — the footer only advances the non-Pro steps and,
  // once Pro, offers "Start writing".
  // Front door: the account is the default path, so the welcome CTA leads to
  // sign in / sign up; "Continue without an account" is the quiet branch
  // inside the step (mirrors Swift).
  const primaryLabel =
    step === "welcome"
      ? frontDoor
        ? t("onboarding.signInCta")
        : t("onboarding.getStarted")
      : step === "pro"
        ? t("onboarding.startWriting")
        : t("onboarding.next");

  return (
    <div
      data-testid="onboarding-overlay"
      // A click anywhere on the welcome page skips its intro to the finished
      // state. The chrome is inert meanwhile, so the click does nothing else.
      onClick={chromeHidden ? skipIntro : undefined}
      onAnimationEnd={(e) => {
        // The overlay's own fade-out finished — close for real. Guard against
        // bubbled child animations and the mount fade-in.
        if (e.target !== e.currentTarget || !closing) return;
        closeOnboarding();
      }}
      className={[
        "fixed inset-0 z-[150] bg-canvas font-chrome flex flex-col overflow-hidden",
        closing
          ? "motion-safe:animate-out motion-safe:fade-out motion-safe:fill-mode-forwards motion-safe:duration-200"
          : "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300",
      ].join(" ")}
    >
      {/* Header: back, progress dots, skip — pinned above the scroll area.
        Wider column than the content so the dots + skip frame the narrower
        reading column on desktop. */}
      <div
        inert={chromeHidden}
        className={`relative z-10 bg-canvas flex items-center gap-4 w-full max-w-[880px] mx-auto px-6 pt-5 pb-2 shrink-0 after:absolute after:inset-x-0 after:top-full after:h-6 after:bg-gradient-to-b after:from-canvas after:to-transparent after:pointer-events-none ${chromeClass}`}
      >
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("onboarding.back")}
            onClick={goBack}
            disabled={!previousOnboardingStep(step)}
            className="border-none"
          >
            <Icon name="chevron-left" size={16} />
          </Button>
          <ProgressDots current={ONBOARDING_STEPS.indexOf(step)} />
          <div className="flex-1" />
          <Button
            variant="ghost"
            className="border-none"
            size="sm"
            onClick={() => complete("skipped")}
          >
            {t("onboarding.skip")}
          </Button>
        </div>
      {/* Step content — the only scrollable region, between the pinned
        header and footer, so `min-h-full` centers short steps exactly. */}
      <div className="flex-1 overflow-y-auto">
        {/* Keyed by step so each committed change remounts with an enter
          animation; while `pendingStep` is set the old step plays its exit.
          The initial step (direction null) mounts without an enter animation
          — the overlay fade-in covers it. */}
        <div
          key={step}
          onAnimationEnd={(e) => {
            // Child animations bubble (e.g. the demo lines) — only commit on
            // the container's own exit animation.
            if (e.target !== e.currentTarget || !pendingStep) return;
            setStep(pendingStep);
            setPendingStep(null);
          }}
          className={[
            "min-h-full w-full max-w-[640px] mx-auto px-6 py-8 flex flex-col justify-center",
            pendingStep
              ? [
                  "motion-safe:animate-out motion-safe:fade-out motion-safe:fill-mode-forwards motion-safe:duration-200",
                  direction === "back"
                    ? "motion-safe:slide-out-to-right-4"
                    : "motion-safe:slide-out-to-left-4",
                ].join(" ")
              : direction === null
                ? ""
                : [
                    "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300",
                    direction === "forward"
                      ? "motion-safe:slide-in-from-right-4"
                      : "motion-safe:slide-in-from-left-4",
                  ].join(" "),
          ].join(" ")}
        >
          {step === "welcome" && (
            <WelcomeStep
              timeline={introTimeline}
              intro={intro}
              chromeClass={chromeClass}
              onContinueWithoutAccount={frontDoor ? goNext : undefined}
            />
          )}
          {step === "magic" && <MagicStep />}
          {step === "theme" && <ThemeStep />}
          {step === "backup" && (
            <BackupStep consent={!hasUser} />
          )}
          {step === "pro" && <ProStep isPro={isPro} purchase={proPurchase} />}
        </div>
      </div>

      {/* Footer — pinned below the scroll area, matches the content column so
        the CTA stays a sane width. On the Pro step (before buying) the
        purchase CTA lives inline, so the footer collapses to just "continue
        with the free plan". */}
      <div
        inert={chromeHidden}
        className={`relative z-10 bg-canvas w-full max-w-[640px] mx-auto px-6 pt-3 pb-6 shrink-0 flex flex-col gap-1.5 before:absolute before:inset-x-0 before:bottom-full before:h-6 before:bg-gradient-to-t before:from-canvas before:to-transparent before:pointer-events-none ${chromeClass}`}
      >
          {step === "pro" && !isPro ? (
            /* Sticky purchase cluster (mirrors Swift): buy CTA + billing
               disclosure pinned here, plan cards stay in the scroll. Keyed so
               the swap from the plain primary button fades in. */
            <div
              key="pro-footer"
              className="flex flex-col gap-1.5 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
            >
              <ProPurchaseCta purchase={proPurchase} />
              <Button
                variant="ghost"
                size="md"
                className="w-full mt-1"
                onClick={() => complete("continued_free")}
              >
                {t("onboarding.continueFree")}
              </Button>
            </div>
          ) : (
            /* Keyed like the pro cluster so BOTH swap directions crossfade
               (entering the pro step and backing out of it). */
            <div
              key="plain-footer"
              className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
            >
              <Button
                variant="primary"
                size="lg"
                className="w-full whitespace-nowrap"
                onClick={
                  step === "welcome" && frontDoor
                    ? () => navigate("/login")
                    : goNext
                }
              >
                {primaryLabel}
              </Button>
            </div>
          )}
      </div>
    </div>
  );
}

function ProgressDots({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {ONBOARDING_STEPS.map((s, index) => (
        <span
          key={s}
          className={[
            "h-1.5 rounded-full transition-all duration-300",
            index === current
              ? "w-[18px] bg-accent"
              : "w-1.5 bg-muted opacity-50",
          ].join(" ")}
        />
      ))}
    </div>
  );
}

/* ── Welcome ─────────────────────────────────────────────── */

type WelcomeIntroState = "running" | "played" | "skipped";

/**
 * One line of the welcome copy, one span per character so the full text holds
 * its final layout from the first frame (nothing reflows while it is typed).
 * While `typing`, each keystroke appears at its own time and carries the
 * accent caret on its trailing edge until the next one lands (`.onboarding-typed`
 * in index.css); otherwise the same spans rest as plain text. Decorative: the
 * parent exposes the full text to assistive tech.
 */
function TypedLine({
  chars,
  step,
  typing,
}: {
  chars: WelcomeIntroChar[];
  step: number;
  typing: boolean;
}) {
  return (
    <span aria-hidden>
      {chars.map((c, index) => (
        <span
          key={index}
          className={typing && c.at !== null ? "onboarding-typed" : undefined}
          style={
            typing && c.at !== null
              ? { animationDelay: `${c.at}s`, animationDuration: `${step}s` }
              : undefined
          }
        >
          {c.char}
        </span>
      ))}
    </span>
  );
}

function WelcomeStep({
  timeline,
  intro,
  chromeClass,
  onContinueWithoutAccount,
}: {
  timeline: WelcomeIntroTimeline;
  intro: WelcomeIntroState;
  chromeClass: string;
  onContinueWithoutAccount?: () => void;
}) {
  const { t } = useTranslation();
  const running = intro === "running";
  return (
    <div className="flex flex-col items-center text-center gap-7">
      <AnimatedCaliuLogo
        size={88}
        className="text-accent"
        mode={running ? "intro" : "idle"}
      />
      <div className="flex flex-col gap-3">
        {/* The accessible name is the full title from the first frame, however
            much of it has been typed. */}
        <h1
          aria-label={t("onboarding.welcomeTitle")}
          className="font-prose text-3xl font-semibold text-ink"
        >
          <TypedLine
            chars={timeline.title}
            step={WELCOME_INTRO_TITLE_STEP}
            typing={running}
          />
        </h1>
        <p className="font-prose text-lg text-ink-secondary leading-relaxed">
          <span className="sr-only">{t("onboarding.welcomeSubtitle")}</span>
          <TypedLine
            chars={timeline.subtitle}
            step={WELCOME_INTRO_SUBTITLE_STEP}
            typing={running}
          />
        </p>
      </div>
      {/* Front door only: the account is the default path (the footer CTA
          leads to sign in / sign up); this quiet link is the no-account
          branch and simply continues the tour into local mode. It is one of
          the welcome buttons, so it arrives with the rest of the chrome. */}
      {onContinueWithoutAccount && (
        <div inert={running} className={chromeClass}>
          <button
            type="button"
            onClick={onContinueWithoutAccount}
            className="text-[13px] font-medium text-accent hover:underline"
          >
            {t("onboarding.continueWithoutAccount")}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Magic (self-typing markdown demo) ───────────────────── */

type DemoLine =
  | { kind: "heading"; raw: string; display: string }
  | { kind: "body"; raw: string; display: string }
  | { kind: "task"; raw: string; display: string }
  | { kind: "highlight"; raw: string; display: string }
  | { kind: "tags"; raw: string; display: string };

/** Localized marketing-site URL for the "Writing" help section. */
function writingHelpUrl(locale: string): string {
  const webUrl = import.meta.env.VITE_WEB_URL ?? "https://caliuapp.com";
  // The marketing site uses localePrefix "as-needed": `/` stays clean for en.
  const prefix = locale === "en" ? "" : `/${locale}`;
  return `${webUrl}${prefix}/help/writing`;
}

function MagicStep() {
  const { t, i18n } = useTranslation();

  const lines: DemoLine[] = useMemo(() => {
    const title = t("onboarding.demoTitle");
    const body = t("onboarding.demoBody");
    const task = t("onboarding.demoTask");
    const highlight = t("onboarding.demoHighlight");
    const tags = t("onboarding.demoTags");
    return [
      { kind: "heading", raw: `# ${title}`, display: title },
      { kind: "body", raw: body, display: body },
      { kind: "task", raw: `- [ ] ${task}`, display: task },
      // Also a task, with an inline `==…==` mark on one word — shows the
      // checkbox and the highlight landing together. `demoHighlight` carries the
      // markers so each locale marks its own "before".
      { kind: "highlight", raw: `- [ ] ${highlight}`, display: highlight },
      { kind: "tags", raw: tags, display: tags },
    ];
  }, [t]);

  const [typedCounts, setTypedCounts] = useState<number[]>(() =>
    lines.map(() => 0),
  );
  const [formatted, setFormatted] = useState<Set<number>>(new Set());
  const [taskChecked, setTaskChecked] = useState(false);
  const [showsReplay, setShowsReplay] = useState(false);
  const [run, setRun] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    setTypedCounts(lines.map(() => 0));
    setFormatted(new Set());
    setTaskChecked(false);
    setShowsReplay(false);

    (async () => {
      await sleep(450);
      for (let index = 0; index < lines.length; index++) {
        for (let count = 1; count <= lines[index].raw.length; count++) {
          if (cancelled) return;
          setTypedCounts((prev) =>
            prev.map((c, i) => (i === index ? count : c)),
          );
          await sleep(26);
        }
        await sleep(180);
        if (cancelled) return;
        setFormatted((prev) => new Set(prev).add(index));
        await sleep(260);
      }
      await sleep(500);
      if (cancelled) return;
      setTaskChecked(true);
      await sleep(400);
      if (cancelled) return;
      setShowsReplay(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [lines, run]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2.5">
        <h2 className="font-prose text-3xl font-semibold text-ink">
          {t("onboarding.magicTitle")}
        </h2>
        <p className="text-[15px] text-ink-secondary leading-relaxed">
          {t("onboarding.magicSubtitle")}
        </p>
        <a
          href={writingHelpUrl(i18n.resolvedLanguage ?? "en")}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-fit items-center gap-1 text-[13px] font-medium text-accent hover:underline"
        >
          {t("onboarding.magicLearnMore")}
          <Icon name="arrow-up-right" size={13} />
        </a>
      </div>

      <div className="min-h-[250px] rounded-2xl border border-border-light bg-canvas shadow-sm p-5 flex flex-col gap-3.5">
        {lines.map((line, index) => (
          <DemoLineView
            key={line.kind}
            line={line}
            typed={typedCounts[index]}
            isFormatted={formatted.has(index)}
            taskChecked={taskChecked}
          />
        ))}
      </div>

      <div className="flex justify-center h-8">
        {showsReplay && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRun((r) => r + 1)}
          >
            <Icon name="restore" size={14} />
            {t("onboarding.replay")}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Renders `**bold**` spans of a demo line without a markdown parser. */
function BoldableText({ text }: { text: string }) {
  const parts = text.split("**");
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <strong key={i}>{part}</strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

/** Renders `==mark==` spans of a demo line without a markdown parser. */
function HighlightableText({ text }: { text: string }) {
  const parts = text.split("==");
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="onboarding-highlight text-ink">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

/** The demo task checkbox, shared by the task and highlight-task lines. */
function DemoCheckbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={[
        "inline-flex items-center justify-center w-[17px] h-[17px] rounded-[4px] border translate-y-[2px] transition-colors duration-200",
        checked
          ? "bg-accent border-accent text-on-accent"
          : "border-border bg-canvas",
      ].join(" ")}
    >
      {checked && <Icon name="check" size={12} />}
    </span>
  );
}

function DemoLineView({
  line,
  typed,
  isFormatted,
  taskChecked,
}: {
  line: DemoLine;
  typed: number;
  isFormatted: boolean;
  taskChecked: boolean;
}) {
  if (!isFormatted) {
    if (typed <= 0) return null;
    return (
      <p className="font-prose text-base text-ink-secondary">
        {line.raw.slice(0, typed)}
        <span className="inline-block w-[2px] h-[1.1em] align-text-bottom bg-accent" />
      </p>
    );
  }

  switch (line.kind) {
    case "heading":
      return (
        <h3 className="font-headings text-2xl font-bold text-ink animate-in fade-in duration-300">
          {line.display}
        </h3>
      );
    case "body":
      return (
        <p className="font-prose text-base text-ink animate-in fade-in duration-300">
          <BoldableText text={line.display} />
        </p>
      );
    case "task":
      return (
        <div className="flex items-baseline gap-2.5 animate-in fade-in duration-300">
          <DemoCheckbox checked={taskChecked} />
          <span className="font-prose text-base text-ink">{line.display}</span>
        </div>
      );
    case "highlight":
      return (
        <div className="flex items-baseline gap-2.5 animate-in fade-in duration-300">
          {/* Second task deliberately stays open — a checklist mid-progress
              reads more alive than all-done. Only the first task ticks. */}
          <DemoCheckbox checked={false} />
          <span className="font-prose text-base text-ink">
            <HighlightableText text={line.display} />
          </span>
        </div>
      );
    case "tags":
      return (
        <div className="flex gap-2 animate-in fade-in duration-300">
          {line.display.split(" ").map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-accent-bg text-accent-text px-2.5 py-0.5 text-[13px] font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      );
  }
}

/* ── Theme ───────────────────────────────────────────────── */

function ThemeStep() {
  const { t } = useTranslation();
  const { theme, setTheme, colorThemeLight, colorThemeDark, setColorTheme } = useUIStore();
  const openPaywall = useUIStore((s) => s.openPaywall);
  const isPro = useIsPro();
  const isDark = useIsDark();
  // Onboarding always sets both modes — day/night pairing is a later,
  // deliberate choice made from the theme picker.
  const colorTheme = isDark ? colorThemeDark : colorThemeLight;

  const pickTheme = (id: string) => {
    if (canUseTheme(isPro, id)) setColorTheme(id);
    else openPaywall("onboarding_theme");
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2.5">
        <h2 className="font-prose text-3xl font-semibold text-ink">
          {t("onboarding.themeTitle")}
        </h2>
        <p className="text-[15px] text-ink-secondary leading-relaxed">
          {t("onboarding.themeSubtitle")}
        </p>
      </div>

      <SegmentedControl<Theme>
        value={theme}
        onChange={setTheme}
        options={[
          { value: "light", label: t("themes.light") },
          { value: "dark", label: t("themes.dark") },
          { value: "system", label: t("themes.system") },
        ]}
      />

      <div className="grid grid-cols-2 gap-3">
        {themes.map((th) => (
          <ThemePreviewCard
            key={th.id}
            theme={th}
            isDark={isDark}
            active={colorTheme === th.id}
            locked={!canUseTheme(isPro, th.id)}
            onClick={() => pickTheme(th.id)}
          />
        ))}
      </div>
    </div>
  );
}

/* ── Backup (your notes live here) ───────────────────────── */

/**
 * The persistence story: a note travels device → synced → all your devices.
 * Signed out it also carries the sync status card, which is status plus a
 * sign-in door: sync is a consequence of having an account, never a toggle.
 * For signed-in users (and replays) the step is purely informational.
 */
function BackupStep({ consent }: { consent: boolean }) {
  const { limit: noteLimit } = useNoteLimit();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // The step itself is the upgrade prompt on the front door.
    if (consent) track(AnalyticsEvent.LocalModeUpgradePrompted, { source: "onboarding" });
    // Fire once per mount; `consent` is stable for the overlay's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stages = [
    { icon: <Icon name="smartphone" size={22} />, label: t("onboarding.backupStageDevice") },
    { icon: <Icon name="cloud-upload" size={22} />, label: t("onboarding.backupStageSafe") },
    { icon: <Icon name="devices" size={22} />, label: t("onboarding.backupStageEverywhere") },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2.5">
        <h2 className="font-prose text-3xl font-semibold text-ink">
          {t("onboarding.backupTitle")}
        </h2>
        <p className="text-[15px] text-ink-secondary leading-relaxed">
          {t("onboarding.backupSubtitle")}
        </p>
      </div>

      {/* The traveling-note diagram: three stages on one line, a note dot
          sliding across them (CSS keyframe `onboarding-travel`). */}
      <div className="relative rounded-2xl border border-border-light bg-canvas shadow-sm px-6 py-7">
        <div className="relative flex items-start justify-between">
          <div
            aria-hidden
            className="absolute left-[10%] right-[10%] top-[22px] h-px bg-border"
          />
          <div
            aria-hidden
            className="onboarding-travel-dot absolute top-[18px] w-2.5 h-2.5 rounded-full bg-accent motion-reduce:hidden"
          />
          {stages.map((stage) => (
            <div
              key={stage.label}
              className="relative z-10 flex flex-col items-center gap-2 w-1/3"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-border-light bg-sidebar text-accent">
                {stage.icon}
              </span>
              <span className="text-[12px] text-ink-secondary text-center leading-snug">
                {stage.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {consent ? (
        // Sync status, not a consent moment: an account means sync, so the
        // card is status plus one door. The free note limit line sits under
        // it: the no-account path is the one it describes.
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3 w-full rounded-xl border border-border-light bg-sidebar p-4">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent-bg text-accent shrink-0">
              <Icon name="cloud-off" size={18} />
            </span>
            <span className="flex flex-col gap-0.5 flex-1 min-w-0">
              <span className="text-[14px] font-medium text-ink">
                {t("onboarding.syncOffTitle")}
              </span>
              <span className="text-[13px] text-ink-secondary leading-snug">
                {t("onboarding.syncOffBody")}
              </span>
            </span>
            <Button variant="primary" size="sm" onClick={() => navigate("/login")}>
              {t("backup.signIn")}
            </Button>
          </div>
          {/* Only where a limit actually applies (new device after day D), the
              same gate as the native sync step; a legacy device keeps unlimited
              local notes and must not be told otherwise. */}
          {noteLimit != null && (
            <p className="text-[13px] text-muted text-center leading-snug">
              {t("onboarding.freeNotes", { limit: noteLimit })}
            </p>
          )}
        </div>
      ) : (
        <p className="text-[14px] text-ink-secondary text-center leading-relaxed">
          {t("onboarding.backupSignedIn")}
        </p>
      )}
    </div>
  );
}

/* ── Pro finale ──────────────────────────────────────────── */

function ProStep({
  isPro,
  purchase,
}: {
  isPro: boolean;
  purchase: ProPurchaseState;
}) {
  const { t } = useTranslation();

  if (isPro) {
    return (
      <div className="flex flex-col items-center text-center gap-6">
        <AnimatedCaliuLogo size={72} className="text-accent" mode="idle" />
        <div className="flex flex-col gap-3">
          <h2 className="font-prose text-3xl font-semibold text-ink">
            {t("onboarding.allSetTitle")}
          </h2>
          <p className="font-prose text-lg text-ink-secondary leading-relaxed">
            {t("onboarding.allSetSubtitle")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col items-center gap-7">
      <ProLockup />

      <div className="flex flex-col gap-2.5 text-center">
        <p className="text-[15px] text-ink-secondary leading-relaxed">
          {t("onboarding.proSubtitle")}
        </p>
      </div>

      <ProFeatureListBox />

      <p className="text-[13px] text-muted text-center">
        {t("onboarding.proTrust")}
      </p>

      {/* Plan cards only (mirrors Swift's OnboardingProView): the buy CTA +
          billing disclosure are pinned in the overlay footer so they never
          scroll out of reach. On success `isPro` flips and this step
          re-renders to the "all set" send-off. */}
      <div className="w-full">
        <ProPlanCards purchase={purchase} />
      </div>
    </div>
  );
}
